/*
  Animated favicon — sweeps the same diagonal gold "shine" across the brand
  mark that logo-animation.css runs on the header logo, so the tab icon speaks
  the same visual language as the site.

  Notes on the choices here:

  - The source image URL is read off the <link rel="icon" id="favicon-static">
    written by layout/theme.liquid. It is never hardcoded a second time, so the
    tab icon and the static fallback can't drift apart.

  - Each frame REPLACES the <link rel="icon"> element (remove + insert a new
    one) instead of mutating .href on the existing node. Several browsers
    (Firefox most notoriously) don't reliably re-fetch the tab icon when only
    the href attribute of a live <link> changes; swapping the element is the
    portable way.

  - The shine is composited with 'lighter' (additive) through a scratch canvas
    that has been clipped to the logo's own alpha via 'destination-in'. Canvas
    'overlay' was the first thing tried, but canvas blend modes still use
    source-over alpha compositing: over the transparent padding around the
    mark the gradient paints straight onto nothing and the icon reads as a
    glowing square. Clipping to the logo alpha first is the canvas equivalent
    of the -webkit-mask-image trick in logo-animation.css, and the same square
    halo it was added to solve.

  - Repaints are throttled to FRAME_MS (~160ms). A favicon has no use for
    60fps and toDataURL() is not free.

  - The loop stops completely while the tab is hidden and while
    prefers-reduced-motion is set. Any failure (image won't load, canvas
    throws, tainted canvas) aborts quietly and leaves the static favicon that
    theme.liquid rendered exactly where it is.
*/
(function () {
  'use strict';

  var SIZE = 64; // canvas edge, px — crisp enough for 32/48px tab icons, cheap to redraw
  var FRAME_MS = 160; // repaint throttle (~6 fps)
  var CYCLE_MS = 2400; // one full sweep across the mark
  var ICON_SELECTOR = 'link[rel~="icon"]';
  var ANIMATED_ID = 'favicon-animated';

  var staticLink = document.getElementById('favicon-static') || document.querySelector(ICON_SELECTOR);
  if (!staticLink || !staticLink.href) return;

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    // Reduced motion: never start the loop. The corrected static favicon stays.
    return;
  }

  var sourceUrl = staticLink.href;
  var base;
  var scratch;
  var baseCtx;
  var scratchCtx;
  var timer = null;
  var started = 0;
  var image = null;

  function fail() {
    stop();
    document.removeEventListener('visibilitychange', onVisibility);
  }

  function drawFrame() {
    // phase 0..1 of the sweep, looping
    var phase = ((Date.now() - started) % CYCLE_MS) / CYCLE_MS;

    baseCtx.setTransform(1, 0, 0, 1, 0, 0);
    baseCtx.globalCompositeOperation = 'source-over';
    baseCtx.clearRect(0, 0, SIZE, SIZE);
    baseCtx.drawImage(image, 0, 0, SIZE, SIZE);

    // Shine band on the scratch layer, angled to echo the header's
    // linear-gradient(115deg, ...). The travel range is derived by projecting
    // the canvas onto the sweep axis (rather than guessed from the edge
    // length) so the band starts fully clear of one corner and ends fully
    // clear of the opposite one — an earlier guessed range parked the band
    // off-canvas for a third of the cycle, producing byte-identical frames.
    var band = SIZE * 0.9;
    var dx = Math.cos((115 * Math.PI) / 180);
    var dy = Math.sin((115 * Math.PI) / 180);
    var mid = SIZE * 0.5;
    var axisMid = mid * dx + mid * dy; // canvas centre projected onto the axis
    var axisHalf = mid * (Math.abs(dx) + Math.abs(dy)); // half the canvas' extent along it
    var pos = axisMid - axisHalf - band + phase * (axisHalf * 2 + band);
    var cx = mid + (pos - axisMid) * dx;
    var cy = mid + (pos - axisMid) * dy;

    var grad = scratchCtx.createLinearGradient(cx, cy, cx + band * dx, cy + band * dy);
    grad.addColorStop(0, 'rgba(201, 166, 107, 0)'); // #c9a66b, transparent
    grad.addColorStop(0.48, 'rgba(255, 255, 255, 0.55)');
    grad.addColorStop(0.72, 'rgba(221, 184, 119, 0.28)'); // #ddb877
    grad.addColorStop(1, 'rgba(221, 184, 119, 0)');

    scratchCtx.setTransform(1, 0, 0, 1, 0, 0);
    scratchCtx.globalCompositeOperation = 'source-over';
    scratchCtx.clearRect(0, 0, SIZE, SIZE);
    scratchCtx.fillStyle = grad;
    scratchCtx.fillRect(0, 0, SIZE, SIZE);
    // Clip the shine to the mark's own alpha — no glow over the transparent padding.
    scratchCtx.globalCompositeOperation = 'destination-in';
    scratchCtx.drawImage(image, 0, 0, SIZE, SIZE);
    scratchCtx.globalCompositeOperation = 'source-over';

    baseCtx.globalCompositeOperation = 'lighter';
    baseCtx.drawImage(scratch, 0, 0);
    baseCtx.globalCompositeOperation = 'source-over';

    return base.toDataURL('image/png');
  }

  function swapIcon(dataUrl) {
    var links = document.querySelectorAll(ICON_SELECTOR);
    var i;
    for (i = 0; i < links.length; i++) {
      if (links[i].parentNode) links[i].parentNode.removeChild(links[i]);
    }
    var link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    link.id = ANIMATED_ID;
    link.href = dataUrl;
    (document.head || document.getElementsByTagName('head')[0]).appendChild(link);
  }

  function tick() {
    try {
      swapIcon(drawFrame());
    } catch (e) {
      // Tainted canvas, lost context, anything else — stop and leave whatever
      // icon is currently in the document alone.
      fail();
    }
  }

  function stop() {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  }

  function start() {
    if (timer !== null) return;
    timer = setInterval(tick, FRAME_MS);
    tick();
  }

  function onVisibility() {
    if (document.visibilityState === 'hidden') {
      stop();
    } else {
      start();
    }
  }

  function init() {
    try {
      base = document.createElement('canvas');
      scratch = document.createElement('canvas');
      base.width = base.height = scratch.width = scratch.height = SIZE;
      baseCtx = base.getContext('2d');
      scratchCtx = scratch.getContext('2d');
      if (!baseCtx || !scratchCtx) return;
    } catch (e) {
      return;
    }

    image = new Image();
    // Theme assets are served with access-control-allow-origin: * — requesting
    // them anonymously keeps the canvas untainted so toDataURL() can run.
    image.crossOrigin = 'anonymous';
    image.onload = function () {
      if (!image.naturalWidth) return; // decoded to nothing — keep the static icon
      started = Date.now();
      document.addEventListener('visibilitychange', onVisibility);
      if (document.visibilityState !== 'hidden') start();
    };
    image.onerror = function () {
      // Static favicon in the document stays as-is.
      image = null;
    };
    image.src = sourceUrl;
  }

  init();
})();
